import { useId } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useModaleAccessibile } from '../../hooks/useModaleAccessibile';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';

/**
 * La domanda che precede un'azione che cambia la partita.
 *
 * Nasce dentro la barra di gioco, per il ritiro, e ci è rimasta finché i clienti
 * erano due (ritiro e patta in presenza). Il terzo — il boost d'arena, che paga
 * l'orologio al primo tocco — sta in un altro componente e in un'altra cartella:
 * o si esportava questo, o nasceva la seconda copia destinata a divergere alla
 * prima modifica.
 *
 * `role="alertdialog"` e non `dialog`: è una domanda che interrompe («abbandoni
 * davvero?»), e quel ruolo dice allo screen reader di annunciare subito il
 * messaggio invece di aspettare che l'utente lo cerchi.
 *
 * Il fuoco entra e non esce (`useModaleAccessibile`): senza, si continuava a
 * tabulare sulla damiera sotto — dove il primo bersaglio utile è di nuovo
 * «abbandona». Lo stesso hook porta l'Escape, che prima era un effetto della
 * barra: stando qui vale per chiunque monti questa conferma. Il gesto
 * «indietro» fa la stessa cosa (`useOverlayBackButton`).
 *
 * Il velo e il riquadro sono `.auth-modal-overlay`/`.auth-modal`, che vivono in
 * `components/auth/AuthModal.css`: regge perché `App.tsx` importa `AuthModal` in
 * modo STATICO, quindi quel foglio è nel bundle principale ovunque. Se un domani
 * quell'import diventasse `lazy()`, questa conferma resterebbe senza riquadro —
 * testo nudo sopra la pagina — su ogni schermata che non abbia mai aperto il
 * modale di accesso.
 */
export interface ConfirmDialogProps {
  title: string;
  /** Il corpo: una stringa, o più righe già composte da chi chiama. */
  message: ReactNode;
  confirmLabel: string;
  confirmTestId: string;
  cancelTestId: string;
  onConfirm: () => void;
  onCancel: () => void;
  /**
   * Il colore del bottone che conferma.
   *
   * `danger` (rosso) è il default perché è da lì che questo componente viene, ed
   * è giusto per ciò che non si torna indietro: ritiro, patta in presenza.
   * `accent` è per le scelte reversibili — il boost si spegne fino alla propria
   * prima mossa — dove il rosso prometterebbe una gravità che non c'è.
   */
  confirmTone?: 'danger' | 'accent';
  /** Solo dove «Annulla» non è la parola giusta; altrimenti resta il default. */
  cancelLabel?: string;
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  confirmTestId,
  cancelTestId,
  onConfirm,
  onCancel,
  confirmTone = 'danger',
  cancelLabel,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const riferimentoConferma = useModaleAccessibile<HTMLDivElement>(true, onCancel);
  /*
   * Il gesto «indietro» del telefono chiude la domanda, come fanno gli altri
   * diciotto overlay dell'app. Senza, un back sopra la conferma del ritiro
   * arrivava alla trappola della schermata di partita — cioè il gesto più
   * usato su mobile faceva una cosa diversa da «annulla», sopra una domanda
   * che chiede proprio se annullare. `true` e non una prop di visibilità: il
   * dialogo è montato solo mentre è aperto.
   */
  useOverlayBackButton(true, onCancel);
  const idTitolo = useId();

  return createPortal(
    <div className="auth-modal-overlay auth-modal-overlay--conferma" onClick={onCancel}>
      <div
        ref={riferimentoConferma}
        className="auth-modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={idTitolo}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 360, textAlign: 'center' }}
      >
        <h2 id={idTitolo} style={{ marginBottom: 'var(--spacing-md)', fontSize: '1.3rem' }}>{title}</h2>
        <div style={{
          color: 'var(--color-text-secondary)',
          fontSize: '0.95rem',
          lineHeight: 1.5,
          marginBottom: 'var(--spacing-lg)',
        }}>
          {message}
        </div>
        <div style={{ display: 'flex', gap: 'var(--spacing-md)' }}>
          <button
            type="button"
            data-testid={confirmTestId}
            onClick={onConfirm}
            style={{
              flex: 1,
              padding: 'var(--spacing-md)',
              borderRadius: 8,
              border: 'none',
              /* L'ambra SCURA, la stessa di `--boost-done`: il bianco
                 sull'ambra piena (`--arena-boost-color`) dà 2,1:1, su #A76A00
                 dà 4,6:1. */
              background: confirmTone === 'accent' ? '#A76A00' : 'var(--color-error, #e74c3c)',
              color: 'white',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              minHeight: 44,
            }}
          >
            {confirmLabel}
          </button>
          <button
            type="button"
            data-testid={cancelTestId}
            onClick={onCancel}
            style={{
              flex: 1,
              padding: 'var(--spacing-md)',
              borderRadius: 8,
              /* `var(--color-border)` e non il bianco al 20% che c'era: il
                 riquadro è `--color-surface`, cioè BIANCO (l'app non ha tema
                 scuro), e un bordo bianco su bianco non esiste — «Annulla»
                 sembrava testo nudo accanto a un bottone pieno, non la seconda
                 metà di una scelta. */
              border: '1px solid var(--color-border)',
              background: 'transparent',
              color: 'var(--color-text)',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              minHeight: 44,
            }}
          >
            {cancelLabel ?? t('game.confirmLeaveCancel', 'Cancel')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default ConfirmDialog;
